import { useState } from 'react';
import {
  Title1,
  Card,
  Switch,
  SpinButton,
  Label,
  Button,
  Spinner,
  tokens,
} from '@fluentui/react-components';
import {
  SettingsRegular,
  MailRegular,
  ClockRegular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getReminderSettings, updateReminderSetting, type ReminderSetting } from '../services/api';

export default function SettingsPage() {
  return (
    <div style={{ padding: '24px', maxWidth: '800px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <SettingsRegular style={{ fontSize: tokens.fontSizeHero700 }} />
        <Title1>Settings</Title1>
      </div>

      <ReminderSettingsSection />
    </div>
  );
}

function ReminderSettingsSection() {
  const queryClient = useQueryClient();

  const { data: settings, isLoading, isError, refetch } = useQuery({
    queryKey: ['reminderSettings'],
    queryFn: getReminderSettings,
  });

  const mutation = useMutation({
    mutationFn: ({ reminderType, updates }: { reminderType: string; updates: Partial<ReminderSetting> }) =>
      updateReminderSetting(reminderType, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminderSettings'] });
    },
  });

  if (isLoading) {
    return <Spinner label="Loading settings..." />;
  }

  if (isError) {
    // Don't fall back to default values here -- a reader could mistake a default for the
    // actual saved setting. Show the failure plainly instead, with a real retry path.
    return (
      <div className="error-container">
        <p>Failed to load reminder settings. Please try again.</p>
        <Button onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  const docReminder = settings?.find(s => s.reminderType === 'document_reminder');
  const efileReminder = settings?.find(s => s.reminderType === 'efile_rejection_reminder');

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <ClockRegular style={{ fontSize: tokens.fontSizeBase500 }} />
        <h2 style={{ margin: 0, fontSize: '18px' }}>Automated Reminders</h2>
      </div>
      <p style={{ color: 'var(--tl-text-secondary)', marginBottom: '24px', fontSize: tokens.fontSizeBase300 }}>
        Configure automatic reminder notifications sent to clients and preparers.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <ReminderCard
          title="Document Reminders"
          description="Automatically remind clients to upload missing documents when their return is in 'Documents Pending' status."
          icon={<MailRegular />}
          setting={docReminder}
          onUpdate={(updates) => mutation.mutate({ reminderType: 'document_reminder', updates })}
          isPending={mutation.isPending}
        />

        <ReminderCard
          title="E-File Rejection Reminders"
          description="Automatically remind preparers about unresolved e-file rejections."
          icon={<MailRegular />}
          setting={efileReminder}
          onUpdate={(updates) => mutation.mutate({ reminderType: 'efile_rejection_reminder', updates })}
          isPending={mutation.isPending}
        />
      </div>
    </div>
  );
}

function ReminderCard({
  title,
  description,
  icon,
  setting,
  onUpdate,
  isPending,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  setting?: ReminderSetting;
  onUpdate: (updates: Partial<ReminderSetting>) => void;
  isPending: boolean;
}) {
  const [localInterval, setLocalInterval] = useState(setting?.intervalDays ?? 3);
  const [localMax, setLocalMax] = useState(setting?.maxAttempts ?? 5);

  return (
    <Card style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {icon}
          <div>
            <div style={{ fontWeight: 600, fontSize: tokens.fontSizeBase400 }}>{title}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>{description}</div>
          </div>
        </div>
        <Switch
          checked={setting?.isEnabled ?? true}
          onChange={(_, data) => onUpdate({ isEnabled: data.checked })}
          disabled={isPending}
        />
      </div>

      {setting?.isEnabled !== false && (
        <div style={{ display: 'flex', gap: '24px', marginTop: '12px', paddingLeft: '28px' }}>
          <div>
            <Label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
              Send every (days)
            </Label>
            <SpinButton
              value={localInterval}
              min={1}
              max={30}
              onChange={(_, data) => {
                const val = data.value ?? localInterval;
                setLocalInterval(val);
              }}
              style={{ width: '100px' }}
            />
          </div>
          <div>
            <Label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
              Max reminders
            </Label>
            <SpinButton
              value={localMax}
              min={1}
              max={20}
              onChange={(_, data) => {
                const val = data.value ?? localMax;
                setLocalMax(val);
              }}
              style={{ width: '100px' }}
            />
          </div>
          <div style={{ alignSelf: 'flex-end' }}>
            <Button
              appearance="primary"
              size="small"
              onClick={() => onUpdate({ intervalDays: localInterval, maxAttempts: localMax })}
              disabled={isPending || (localInterval === setting?.intervalDays && localMax === setting?.maxAttempts)}
            >
              {isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
